import Phaser from "phaser";
import type { GameSnapshot } from "@gunnrats23/shared";

export class LabScene extends Phaser.Scene {
  private glow?: Phaser.GameObjects.Graphics;
  private screen?: Phaser.GameObjects.Rectangle;
  private snapshot?: GameSnapshot;
  private ticks = 0;

  constructor() {
    super("LabScene");
  }

  create() {
    this.cameras.main.setBackgroundColor("#07151b");
    this.drawStaticLab();
    this.scale.on("resize", () => this.redraw());
  }

  update(_time: number, delta: number) {
    this.ticks += delta;
    if (!this.glow || !this.screen) {
      return;
    }
    const pulse = 0.18 + Math.sin(this.ticks / 380) * 0.04;
    this.screen.setFillStyle(this.snapshot?.phase === "finished" ? 0x102b1b : 0x092a2f, 0.88);
    this.glow.clear();
    this.glow.fillStyle(0x7bd88f, pulse);
    this.glow.fillRoundedRect(92, 72, 392, 236, 8);
    this.glow.lineStyle(1, 0xb4ffef, 0.4 + pulse);
    for (let y = 82; y < 300; y += 12) {
      this.glow.lineBetween(104, y, 472, y);
    }
  }

  updateSnapshot(snapshot: GameSnapshot) {
    this.snapshot = snapshot;
    this.redraw();
  }

  private redraw() {
    this.children.removeAll();
    this.drawStaticLab();
  }

  private drawStaticLab() {
    const width = this.scale.width;
    const height = this.scale.height;
    const g = this.add.graphics();

    g.fillStyle(0x07151b, 1);
    g.fillRect(0, 0, width, height);

    for (let x = 0; x < width; x += 32) {
      g.fillStyle(x % 64 === 0 ? 0x0b2028 : 0x0a1b23, 1);
      g.fillRect(x, 0, 16, height);
    }

    g.fillStyle(0x15242a, 1);
    g.fillRect(0, Math.max(340, height - 160), width, 160);
    g.fillStyle(0x26373d, 1);
    g.fillRect(0, Math.max(330, height - 170), width, 18);

    this.drawCrt(g);
    this.drawLabProps(g, width, height);
    this.drawPixelRats(g);
    this.drawChapterTicker();
  }

  private drawCrt(g: Phaser.GameObjects.Graphics) {
    g.fillStyle(0x2a3137, 1);
    g.fillRoundedRect(58, 42, 472, 318, 18);
    g.fillStyle(0x111820, 1);
    g.fillRoundedRect(78, 60, 432, 268, 12);
    this.screen = this.add.rectangle(288, 190, 392, 236, 0x092a2f, 0.9).setOrigin(0.5);
    this.glow = this.add.graphics();
    g.fillStyle(0x1c252c, 1);
    g.fillRect(208, 360, 160, 30);
    g.fillStyle(0x111820, 1);
    g.fillRect(154, 390, 268, 24);

    g.fillStyle(0xff6b8a, 1);
    g.fillCircle(472, 342, 7);
    g.fillStyle(0x7bd88f, 1);
    g.fillCircle(448, 342, 7);
    g.fillStyle(0xf3c84b, 1);
    g.fillCircle(424, 342, 7);
  }

  private drawLabProps(g: Phaser.GameObjects.Graphics, width: number, height: number) {
    const deskY = Math.max(348, height - 142);
    g.fillStyle(0x0e2b32, 1);
    g.fillRect(590, deskY - 82, 52, 90);
    g.fillStyle(0xb4ffef, 1);
    g.fillRect(604, deskY - 108, 22, 28);
    g.fillStyle(0x75b7ff, 1);
    g.fillRect(610, deskY - 78, 10, 76);

    g.fillStyle(0xff9e64, 1);
    g.fillRect(width - 190, deskY - 42, 24, 48);
    g.fillStyle(0xf3c84b, 1);
    g.fillRect(width - 154, deskY - 72, 24, 78);
    g.fillStyle(0x7bd88f, 1);
    g.fillRect(width - 118, deskY - 34, 24, 40);

    g.fillStyle(0xd7dce2, 1);
    for (let i = 0; i < 6; i += 1) {
      g.fillRect(width - 330 + i * 22, deskY - 26 - (i % 3) * 8, 14, 20 + (i % 3) * 8);
    }
  }

  private drawPixelRats(g: Phaser.GameObjects.Graphics) {
    const y = 436;
    const colors = [0x7bd88f, 0x75b7ff, 0xf3c84b, 0xff6b8a];
    colors.forEach((color, index) => {
      const x = 86 + index * 56;
      g.fillStyle(color, 1);
      g.fillRect(x, y, 28, 18);
      g.fillRect(x + 20, y - 10, 16, 16);
      g.fillStyle(0x07151b, 1);
      g.fillRect(x + 30, y - 4, 3, 3);
      g.fillStyle(0xd7dce2, 1);
      g.fillRect(x - 10, y + 8, 12, 4);
      g.fillStyle(0xffd2df, 1);
      g.fillRect(x + 31, y - 14, 6, 6);
    });
  }

  private drawChapterTicker() {
    const snapshot = this.snapshot;
    const text = snapshot
      ? `${snapshot.gameTitle} // ${snapshot.progressLabel} // ${snapshot.chapter.badge}`
      : "Gunnrats23 // waiting for boot sequence";
    this.add
      .text(104, 104, text, {
        fontFamily: "monospace",
        fontSize: "14px",
        color: "#b4ffef"
      })
      .setDepth(3);
  }
}
